<template>
    <v-row class="white-card" align="center" dense>
        <v-col cols="6" class="h5">
            {{ title }}
        </v-col>
        <v-col cols="6" align="right">
            <group-by-selector
                v-if="!noData"
                @change="currentGroupBy = $event"
                :value="currentGroupBy"
            />
        </v-col>
        <v-col cols="12">
            <div v-if="noData" class="card-no-data">
                <div>
                    <v-icon large color="#6FCF97">mdi-magnify-close</v-icon>
                    <div class="p1 mt-1">
                        {{ $t('common.without_data') }}
                    </div>
                </div>
            </div>
            <line-chart
                v-show="!noData"
                :labels="labels"
                :datasets="datasets"
                style="height: 280px"
                ref="chart"
            />
        </v-col>
    </v-row>
</template>

<script>
import GroupBySelector from "@/components/statistics/GroupBySelector";
import LineChart from "@/components/common/LineChart";
import _groupBy from "lodash/groupBy";
import {db} from "@/utils/Firebase";

export default {
    name: "StatisticsChart",
    components: {LineChart, GroupBySelector},
    props: {
        type: {
            type: String,
            default: '',
        },
        docs: {
            type: Array,
            default: null,
        }
    },
    data() {
        return {
            currentGroupBy: 'daily',
            datasets: [],
            labels: [],
            lineTemplate: {
                borderColor: '#078571',
                pointBackgroundColor: 'rgba(255,255,255, 0.5)',
                pointRadius: 6,
                pointHoverRadius: 6,
                pointBorderWidth: 3,
                borderWidth: 2,
                tension: 0,
            },
            rpeTable: [
                {name: 10, reps: [100, 95, 91, 87, 85, 83, 81, 79]},
                {name: 9.5, reps: [97, 93, 89, 86, 84, 82, 80, 77.5]},
                {name: 9, reps: [95, 91, 87, 85, 83, 81, 79, 76]},
                {name: 8.5, reps: [93, 89, 86, 84, 82, 80, 77.5, 74.5]},
                {name: 8, reps: [91, 87, 85, 83, 81, 79, 76, 73]},
                {name: 7.5, reps: [89, 86, 84, 82, 80, 77.5, 74.5, 71.5]},
                {name: 7, reps: [87, 85, 83, 81, 79, 76, 73, 70]},
            ],
            rirTable: [
                {name: 0, reps: [100, 95, 91, 87, 85, 83, 81, 79]},
                {name: 1, reps: [95, 91, 87, 85, 83, 81, 79, 76]},
                {name: 2, reps: [91, 87, 85, 83, 81, 79, 76, 73]},
                {name: 3, reps: [89, 86, 84, 82, 80, 77.5, 74.5, 71.5]},
                {name: 4, reps: [87, 85, 83, 81, 79, 76, 73, 70]},
            ],
        }
    },
    computed: {
        title(vm = this) {
            let type = vm.type
            if (type === 'duration') type = 'time'
            return vm.$t(`statistics.${type}`)
        },
        needCategory(vm = this) {
            return ['sets_strength', 'sets_speed', 'sets_resistance', 'tonnage', 'distance', 'percentage', 'rpe', 'rir'].includes(vm.type)
        },
        noData(vm = this) {
            return vm.docs.length === 0
        },
        docsGrouped(vm = this) {
            let docs = vm.docs.map(doc => {
                let date = vm.$moment(doc.rawDate.full)
                if (vm.currentGroupBy === 'weekly') date = date.startOf('week')
                if (vm.currentGroupBy === 'monthly') date = date.startOf('month')
                let duration = Number(doc?.advance?.postInfo?.duration || 0)
                let effort = Number(doc?.advance?.postInfo?.effort || 0)

                let payload = {
                    date: date,
                    dateString: date.format('DD MMM').replace('.', ''),
                    duration: duration,
                    effort: effort,
                    charge: duration * effort,
                    sessions: 1,
                }

                if (vm.needCategory) {
                    let sets_strength = 0
                    let sets_speed = 0
                    let sets_resistance = 0
                    let tonnage = 0
                    let distance = 0
                    let percentage = 0
                    let rpe = 0
                    let rir = 0
                    doc.blocks.forEach(b => {
                        b.exercises.forEach(e => {
                            if(e.type !== 'rest') {
                                let sets = Number(e.config?.sets?.length || 0)
                                if (e.category === vm.$t('objectives.strong')) sets_strength += sets
                                if (e.category === vm.$t('objectives.speed')) sets_speed += sets
                                if (e.category === vm.$t('objectives.resistance')) sets_resistance += sets

                                e.config?.sets?.forEach((set,i) => {
                                    if (e.category === vm.$t('objectives.strong')) {
                                        let reps = vm.takeValueFromSet(set, 'reps') || 0
                                        tonnage += (vm.takeValueFromSet(set, 'weight') * reps)
                                    } else if (e.category === vm.$t('objectives.speed') || e.category === vm.$t('objectives.resistance')) {
                                        distance += (vm.takeValueFromSet(set, 'distance') * sets)
                                    } else {
                                        let valuePercentage = vm.takeValueFromSet(set, 'percentage')
                                        percentage += valuePercentage
                                        let valueRpe = vm.takeValueFromSet(set, 'rpe')
                                        rpe += valueRpe ? vm.getValueFromTable(valueRpe, set) : 0
                                        let valueRir = vm.takeValueFromSet(set, 'rir')
                                        rir += valueRir ? vm.getValueFromTable(valueRir, set) : 0
                                    }
                                })
                            }
                        })
                    })
                    payload.sets_strength = sets_strength
                    payload.sets_speed = sets_speed
                    payload.sets_resistance = sets_resistance
                    payload.tonnage = tonnage
                    payload.distance = distance
                    payload.percentage = percentage
                    payload.rpe = rpe
                    payload.rir = rir
                }

                return payload
            })

            if (vm.currentGroupBy !== 'daily') {
                let grouped = _groupBy(docs, d => {
                    return d.dateString
                })

                docs = []
                for (let currentDate in grouped) {
                    if (grouped[currentDate].length === 1) {
                        docs.push(grouped[currentDate][0])
                    } else {
                        let value = 0
                        grouped[currentDate].forEach(group => {
                            value += group[vm.type]
                        })
                        docs.push({
                            date: grouped[currentDate][0].date,
                            dateString: grouped[currentDate][0].dateString,
                            [vm.type]: value,
                        })
                    }
                }
            }

            return docs
        },
    },
    mounted() {
        const vm = this
        vm.loadStats()
    }
    ,
    methods: {
        loadStats() {
            const vm = this
            let data = []
            let labels = []

            // console.log('vm.docsGrouped', vm.type, vm.docsGrouped)

            vm.docsGrouped.forEach(doc => {
                labels.push(doc.dateString)
                data.push(doc[vm.type])
            })

            let gradientFill = this.$refs.chart.$refs.canvas.getContext('2d').createLinearGradient(0, 50, 0, 250);
            gradientFill.addColorStop(0, "#D7FFAF");
            gradientFill.addColorStop(1, "rgba(204, 255, 153, 0)");

            vm.labels = labels

            vm.datasets = [{
                data: data,
                ...vm.lineTemplate,
                backgroundColor: gradientFill,
            }]
        },
        takeValueFromSet(set, attribute) {
            const vm = this
            let defaultValue = 0
            if(attribute === 'rir' || attribute === 'rir') defaultValue = null
            let value = set.data.find(data => data.name === vm.$t(`exercises.${attribute}`))
            return !!value || value === 0 ? Number(value.value || 0) : defaultValue
        },
        getValueFromTable(value, set) {
            const vm = this
            if (vm.type === 'rpe') {
                if (value > 10) value = 10
                if (value < 7) value = 7
            }
            if (vm.type === 'rir') {
                if (value > 4) value = 4
                if (value < 0) value = 0
            }
            let findValueTable = vm[`${vm.type}Table`]?.find(table => table.name === value) || null
            let reps = set.data.find(data => data.name === vm.$t(`exercises.reps`)) || null
            let repsValue = reps && Number(reps.value || 0) - 1 || 0
            if (repsValue > 8) repsValue = 8
            if (repsValue < 0) repsValue = 0
            return findValueTable && reps && findValueTable.reps[repsValue] || 0
        },
    },
    watch: {
        docsGrouped() {
            this.loadStats()
        }
    }
}
</script>

<style scoped>

</style>